import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/DynamicSocialMediaCard.css';
import Button3D from './Button3D';  // Make sure this path is correct

const socialMediaCards = [
  { 
    id: 1, 
    platform: 'YouTube', 
    content: 'Subscribe to our channel!', 
    logo: '/assets/images/youtube-logo.png', 
    link: 'https://www.youtube.com/@quantumframestudio7',
    theme: 'youtube'
  },
  { 
    id: 2, 
    platform: 'Instagram', 
    content: 'Follow our Instagram!', 
    logo: '/assets/images/instagram-logo.png', 
    link: 'https://www.instagram.com/quantumframe_studio/',
    theme: 'instagram'
  },
  { 
    id: 3, 
    platform: 'Twitter', 
    content: 'Follow us on Twitter!', 
    logo: '/assets/images/twitter-logo.png', 
    link: 'https://x.com/quantumframe_s',
    theme: 'twitter'
  },
  { 
    id: 4, 
    platform: 'Facebook', 
    content: 'Like our Facebook page!', 
    logo: '/assets/images/facebook-logo.png', 
    link: 'https://www.facebook.com/people/Quantum-Frame-Studio/61580946590092/',
    theme: 'facebook'
  },
  { 
    id: 5, 
    platform: 'LinkedIn', 
    content: 'Connect on LinkedIn!', 
    logo: '/assets/images/linkedin-logo.png', 
    link: 'https://www.linkedin.com/company/quantumframestudio/',
    theme: 'linkedin'
  },
];

function DynamicSocialMediaCard() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [timeLeft, setTimeLeft] = useState(5);
  const [isDragging, setIsDragging] = useState(false);
  const [startAngle, setStartAngle] = useState(0);
  const containerRef = useRef(null);

  const anglePerCard = 360 / socialMediaCards.length;

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
    setRotationAngle(-anglePerCard * index);
    setTimeLeft(5);
  }, [anglePerCard]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % socialMediaCards.length);
    setRotationAngle((prevAngle) => prevAngle - anglePerCard);
    setTimeLeft(5);
  }, [anglePerCard]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + socialMediaCards.length) % socialMediaCards.length);
    setRotationAngle((prevAngle) => prevAngle + anglePerCard);
    setTimeLeft(5);
  }, [anglePerCard]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isDragging) {
        setTimeLeft((prevTime) => {
          if (prevTime === 1) {
            nextSlide();
            return 5;
          }
          return prevTime - 1;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [nextSlide, isDragging]);

  const handleCardClick = (link) => {
    if (!isDragging) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.card')) {
      setIsDragging(true);
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setStartAngle(Math.atan2(e.clientY - centerY, e.clientX - centerX));
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
      const deltaAngle = angle - startAngle;
      setRotationAngle((prevAngle) => prevAngle + deltaAngle * (180 / Math.PI));
      setStartAngle(angle);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      const nearestIndex = Math.round(-rotationAngle / anglePerCard) % socialMediaCards.length;
      goToSlide((nearestIndex + socialMediaCards.length) % socialMediaCards.length);
    }
  };

  return (
    <div 
      className="social-media-card-container" 
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <button 
        className={`card-prev-btn ${socialMediaCards[currentIndex].theme}`} 
        onClick={prevSlide}
      >
        ❮
      </button>
      <button 
        className={`card-next-btn ${socialMediaCards[currentIndex].theme}`} 
        onClick={nextSlide}
      >
        ❯
      </button>
      <div
        className="card-wrapper"
        style={{
          transform: `rotateY(${rotationAngle}deg)`,
          transition: isDragging ? 'none' : 'transform 0.5s ease-out',
        }}
      >
        {socialMediaCards.map((card, index) => (
          <div
            key={card.id}
            className={`card ${card.theme} ${index === currentIndex ? 'active' : ''}`}
            style={{
              transform: `rotateY(${anglePerCard * index}deg) translateZ(250px)`,
              transition: isDragging ? 'none' : 'transform 0.5s ease-out, opacity 0.5s ease',
            }}
          >
            <div className="card-content">
              <img src={card.logo} alt={`${card.platform} logo`} className="platform-logo" />
              <h3>{card.platform}</h3>
              <p>{card.content}</p>
              <Button3D onClick={() => handleCardClick(card.link)}>
                Visit {card.platform}
              </Button3D>
            </div>
          </div>
        ))}
      </div>
      <div className="timer-lines">
        {socialMediaCards.map((card, index) => (
          <div
            key={index}
            className={`timer-line ${card.theme} ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          >
            <div 
              className="timer-progress"
              style={{
                transform: index === currentIndex ? `scaleX(${1 - (timeLeft - 1) / 4})` : 'scaleX(0)',
                transition: index === currentIndex ? 'transform 1s linear' : 'none'
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default DynamicSocialMediaCard;